import { WebConnector } from './spiderweb-connector';
import { WebNode } from './spiderweb-node';
import { WebOverlapLayers } from './spiderweb-overlap-layers';
import {
	VIEWBOX_SCALE,
	clientPointToSpiderWebPercent,
	type NodeCenterPct,
} from './spiderweb-utils';

import type {
	FillColor,
	ResolvedConnector,
	SpiderWebCanvasClickPct,
	SpiderWebNodeFocus,
	WebNodeConfig,
} from './spiderweb-types';
import type { NodePositionPct } from './use-spiderweb-phase-positions';
import type { AspectCorrection } from './use-spiderweb-viewport-aspect';

function toCenterPct(layout: { x: number; y: number } | undefined): NodeCenterPct | undefined {
	if (!layout) return undefined;
	return { x: layout.x, y: layout.y };
}

export type SpiderWebContainerGraphProps = {
	viewBoxHeight: number;
	useViewportDimensions?: boolean;
	aspectCorrection?: AspectCorrection;
	resolvedConnectors: ResolvedConnector[];
	nodes: WebNodeConfig[];
	animated: boolean;
	skipLoadEntrance: boolean;
	connectorColor: FillColor;
	connectorWidth: number;
	connectorEntranceDelay: (index: number) => number;
	nodeEntranceDelay: (index: number) => number;
	overlapEntranceDelay: number;
	nodeLoadEntranceDurationSec: number;
	connectorLoadEntranceSolidDrawDurationSec: number;
	connectorLoadEntranceDashedFadeDurationSec: number;
	connectorLoadEntranceAnimatedShapeOpacityDurationSec: number;
	connectorLoadEntranceAnimatedShapeAppearOffsetSec: number;
	overlapLoadEntranceFadeDurationSec: number;
	fontFamily: 'charlie' | 'monospace';
	expandingNodeId: string | null;
	onNodeActivate: (node: WebNodeConfig) => void;
	allowNodeActivationWithoutModal: boolean;
	nodePositions: ReadonlyMap<string, NodePositionPct>;
	onSvgCanvasClick?: (pct: SpiderWebCanvasClickPct) => void;
	focusedConnector?: { from: string; to: string };
	nodeFocus?: SpiderWebNodeFocus;
};

export const SpiderWebContainerGraph = ({
	viewBoxHeight,
	useViewportDimensions = false,
	aspectCorrection,
	resolvedConnectors,
	nodes,
	animated,
	skipLoadEntrance,
	connectorColor,
	connectorWidth,
	connectorEntranceDelay,
	nodeEntranceDelay,
	overlapEntranceDelay,
	nodeLoadEntranceDurationSec,
	connectorLoadEntranceSolidDrawDurationSec,
	connectorLoadEntranceDashedFadeDurationSec,
	connectorLoadEntranceAnimatedShapeOpacityDurationSec,
	connectorLoadEntranceAnimatedShapeAppearOffsetSec,
	overlapLoadEntranceFadeDurationSec,
	fontFamily,
	expandingNodeId,
	onNodeActivate,
	allowNodeActivationWithoutModal,
	nodePositions,
	onSvgCanvasClick,
	focusedConnector,
	nodeFocus,
}: SpiderWebContainerGraphProps) => (
	<svg
		viewBox={`0 0 ${VIEWBOX_SCALE} ${viewBoxHeight}`}
		preserveAspectRatio={useViewportDimensions ? 'none' : 'xMidYMid meet'}
		style={{
			width: '100%',
			height: '100%',
			display: 'block',
		}}
	>
		{onSvgCanvasClick ? (
			<rect
				x={0}
				y={0}
				width={VIEWBOX_SCALE}
				height={viewBoxHeight}
				fill="transparent"
				pointerEvents="all"
				style={{ cursor: 'inherit' }}
				onClick={(e) => {
					const svg = e.currentTarget.ownerSVGElement;
					if (!svg) {
						return;
					}
					onSvgCanvasClick(clientPointToSpiderWebPercent(svg, e.clientX, e.clientY, viewBoxHeight));
				}}
			/>
		) : null}
		<g>
			{resolvedConnectors.map((connector, i) => (
				<WebConnector
					key={`${connector.from.id}-${connector.to.id}`}
					connector={connector}
					animated={animated}
					delay={connectorEntranceDelay(i)}
					loadEntranceSolidDrawDurationSec={connectorLoadEntranceSolidDrawDurationSec}
					loadEntranceDashedFadeDurationSec={connectorLoadEntranceDashedFadeDurationSec}
					loadEntranceAnimatedShapeOpacityDurationSec={
						connectorLoadEntranceAnimatedShapeOpacityDurationSec
					}
					loadEntranceAnimatedShapeAppearOffsetSec={
						connectorLoadEntranceAnimatedShapeAppearOffsetSec
					}
					skipLoadEntrance={skipLoadEntrance}
					color={connector.color ?? connectorColor}
					strokeWidth={connectorWidth}
					isAnimated={connector.isAnimated}
					animationColor={connector.animationColor}
					viewBoxHeight={viewBoxHeight}
					fromPct={toCenterPct(nodePositions.get(connector.from.id))}
					toPct={toCenterPct(nodePositions.get(connector.to.id))}
					isFocused={
						focusedConnector !== undefined &&
						focusedConnector.from === connector.from.id &&
						focusedConnector.to === connector.to.id
					}
					aspectCorrection={aspectCorrection}
				/>
			))}
		</g>

		<g style={{ isolation: 'isolate' }}>
			{nodes.map((node, i) => (
				<WebNode
					key={node.id}
					node={node}
					animated={animated}
					delay={nodeEntranceDelay(i)}
					loadEntranceDurationSec={nodeLoadEntranceDurationSec}
					skipLoadEntrance={skipLoadEntrance}
					onClick={
						allowNodeActivationWithoutModal || node.modalContent
							? () => onNodeActivate(node)
							: undefined
					}
					fontFamily={fontFamily}
					isExpanding={expandingNodeId === node.id}
					viewBoxHeight={viewBoxHeight}
					aspectCorrection={aspectCorrection}
					layoutPct={nodePositions.get(node.id)}
					focusVariant={nodeFocus?.nodeId === node.id ? nodeFocus.variant : undefined}
				/>
			))}
		</g>

		<WebOverlapLayers
			nodes={nodes}
			viewBoxHeight={viewBoxHeight}
			animated={animated}
			delay={overlapEntranceDelay}
			loadEntranceFadeDurationSec={overlapLoadEntranceFadeDurationSec}
			skipLoadEntrance={skipLoadEntrance}
			nodePositions={nodePositions}
		/>
	</svg>
);
